/* ================================================================ */
// PANEL EDITOR COMPONENT | PanelEditor.jsx
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes, { string } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useSelector } from 'react-redux'
// Material UI
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import Pagination from '@material-ui/lab/Pagination';
import TextField from '@material-ui/core/TextField';
import makeStyles from '@material-ui/styles/makeStyles';
import CameraIcon from '@material-ui/icons/CameraAlt';
import { Paper } from '@material-ui/core';
// Actions
import { updatePanel, createPanel } from '../../actions/Data/Content';
import { updateMapState } from '../../actions/Map';
// Selectors
import { getGlobe } from '../../selectors/Data';
// CKEditor
import CKEditor from '@ckeditor/ckeditor5-react';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import Code from '@ckeditor/ckeditor5-basic-styles/src/code';
import Subscript from '@ckeditor/ckeditor5-basic-styles/src/subscript';
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import Base64UploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/base64uploadadapter';
import GFMDataProcessor from '@ckeditor/ckeditor5-markdown-gfm/src/gfmdataprocessor';
// Hooks
import usePrevious from '@myglobe/core/dist/hooks/usePrevious';
// JSON Editor
import { JsonEditor as JsonEditor } from 'jsoneditor-react';
import 'jsoneditor-react/es/editor.min.css';
import { isNullOrUndefined } from 'util';
/* ========================================== */
// Style Settings
/* ========================================== */
const useStyles = makeStyles(theme => ({
    root: {
        //position: 'relative',
        overflow: 'hidden',
        width: '100%',
        //height: '100%',
        //display: 'flex'
    },
    editToggle: {
        position: 'absolute',
        right: '-1em',
        top: '3em'
    },
    // === Panel Classes ===
    panel: {
        position: 'relative',
        overflow: 'hidden',
        color: theme.palette.text.primary,
        width: '100%',
        height: '100%',
        borderRadius: 0
    },
    editor: {
        color: theme.palette.common.black,
        color:'#FFF',
        position:'fixed',
        left:'60%',
        top:'15%',
        width:'35vw',
        zIndex:1000,
    },
    stateEditor: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    jsonEditor: {
        flex:'1 1 0',
        background:"#FFF",
    },
    ckEditor:{
        color:'#000 !important',
    }
}));
/* ========================================== */
/**
 * Panel Editor
 * ------------
 * Interface for editing slugs, titles and content
 * of a given panel
 *
 * @param {object} props component properties
 * @param {number} index page index
 * @param {boolean} isOpen open status
 * @param {object} panel panel data
 *
 * @param {object} globe globe config
 * @param {string} storyId parent story id
 *
 * @param {func} onChange content change callback
 * @param {func} onUpdate content update callback
 * @param {func} onSave save all panel states to store
 *
 * @param {func} createPanel creates a new panel in panels store
 * @param {func} updatePanel updates existing panel in panels store
 */
const PanelEditor = (props) => {
    const classes = useStyles();
    const {
        index,
        globe,
        storyId,
        panelCount,
        isOpen,
        onChange,
        onUpdate,
        onCancel,
        onSave,
        onPanelChange,
        createPanel,
        updatePanel,
        updateMapState
    } = props;
    const [content, setContent] = useState(props.panel?.content);
    const [panel, setPanel] = useState(props.panel);    
    const [viewState, setViewState] = useState(props.panel.viewState);
    const curMapView = useSelector(state => state.Map.viewState);
    const visLayers = useSelector(state => state.Data.layers.byId);
    const jsonEditorRef = useRef();
    // Simple plugin which loads the data processor.
    function Markdown( editor ) {
        editor.data.processor = new GFMDataProcessor( editor.editing.view.document );
    }

    /*useEffect(() => {

    }, [index]);*/

    const editorConfig = {
        plugins: [
            Markdown,
            Essentials,
            Heading,
            Paragraph,
            Bold,
            Italic,
            Underline,
            Strikethrough,
            Image,
            ImageToolbar,
            ImageResize,
            Code,
            Subscript,
            Superscript,
            Base64UploadAdapter
        ],
        toolbar: {
            items: [
                'heading',
                '|',
                'bold',
                'italic',
                'link',
                'bulletedList',
                'numberedList',
                'blockQuote',
                'undo',
                'redo',
                'upload'
            ]
        },
        image: {
            toolbar: [
                'imageStyle:full',
                'imageStyle:side',
                '|',
                'imageTextAlternative'
            ]
        },
        language: 'en',/*,
        autosave: {
            waitingTime: 5000, // in ms
            save(editor) {
                return onUpdate(editor.getData());
            }
        },
        */
    };
    /* ======================== */
    // HANDLERS
    /* ======================== */
    /* ========================================== */
    /**
     * Handle Panel Change
     * -------------------
     *
     */
    const handlePanelChange = (event, value) => {
        // Get panel in value range
        onPanelChange && onPanelChange(value);
    };
    const handleCancel = () => {
        onCancel && onCancel();
    };
    const handleJsonChange = (evt) => {
        console.log(evt);
        setViewState(JSON.stringify(evt));
    };
    const handleCaptureState = ()=>{
        let visibleVisLayers = [];
        Object.keys(visLayers).forEach(x=>{
            if(visLayers[x].Visible)
                visibleVisLayers.push(x);
        });
        const newView = {...curMapView, ...{visLayers:visibleVisLayers}};
        jsonEditorRef.current.jsonEditor.set(newView);
        setViewState(JSON.stringify(newView));
    };
    useEffect(()=>{
        if(typeof(viewState)=="string") {
            console.log('here');
            updateMapState({viewState: JSON.parse(viewState)});
        }
        console.log('viewstate change', viewState);
    },[viewState]);
    /* ========================================== */
    /**
     * Handle Save
     * -----------
     * Update a panel with new data
     */
    const handleSave = () => {
        onSave && onSave(index, panel);
        
        isNullOrUndefined(panel.uuid) ? createPanel(panel, storyId, globe.uuid) : updatePanel(panel, globe.name,viewState)
    };
    const handleValue = (event) => {
        const value = event.target.value;
        const stateName = event.target.name;
        setPanel({
            ...panel,
            [stateName]: value
        });
    }
    
    /* ========================================== */
    /**
     * Handle Content Change
     * ---------------------
     * Update content state
     *
     * @param {object} editor ckeditor
     */
    function handleContentChange(event, editor) {
        const data = editor.getData();
        // OnChange Callback
        onChange && onChange(data);
        setContent(data);
        console.log(data);
    }
    /* ========================================== */
    /**
     * Handle Content Update
     * ---------------------
     * Send updated content to callback function and
     * save the current panel content to the panel state
     *
     * @param {object} editor ckeditor
     */
    function handleContentUpdate(event, editor) {
        const data = editor.getData();
        onUpdate && onUpdate(data);
        // Update panel state with active content
        setPanel({
            ...panel,
            content
        });
    }
    return (
        <Paper
            style={{display:isOpen?"block":"none"}}
            className={classes.editor}
        ><div>
            <DialogTitle>
                Panel Editor | {panel.title}
            </DialogTitle>
            <DialogContent>
                <TextField
                    size="small"
                    margin="dense"
                    label="Slug"
                    name="slug"
                    required={true}
                    value={panel.slug}
                    variant="outlined"
                    onChange={handleValue}
                />
                <TextField
                    size="small"
                    margin="dense"
                    label="Title"
                    name="title"
                    required={true}
                    value={panel.title}
                    variant="outlined"
                    onChange={handleValue}
                />
                <TextField
                    size="small"
                    margin="dense"
                    label="Position"
                    name="position"
                    type="number"
                    required={true}
                    value={panel.position}
                    variant="outlined"
                    onChange={handleValue}
                />
                <div className={classes.ckEditor}>
                    <CKEditor key={`panel_${panel.title}_editor`}
                        editor={ClassicEditor}
                        config={editorConfig}
                        data={content}
                        onInit={editor => {
                            // You can store the "editor" and use when it is needed.
                            console.log('Editor is ready to use!', editor);
                        }}
                        onChange={handleContentChange}
                        onBlur={handleContentUpdate}
                        onFocus={(event, editor) => {
                            console.log('Focus.', editor);
                        }}
                    />
                </div>
                <div>
                    <br />
                    <h3>ViewState: {props.panel?.state}</h3>
                    <div className={classes.stateEditor}>
                        <div className={classes.jsonEditor}>
                            {typeof(viewState)=="string"&&<JsonEditor
                            ref={jsonEditorRef}
                            value={JSON.parse(viewState)}
                            onChange={handleJsonChange}
                            mode={JsonEditor.modes.Code}
                            theme="ace/theme/chrome"
                            />}</div>
                        <IconButton onClick={handleCaptureState}><CameraIcon/></IconButton>
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                {panelCount && <Pagination count={panelCount} page={index} onChange={handlePanelChange} />}
                <Button onClick={handleCancel}>Cancel</Button>
                <Button onClick={handleSave}>Save</Button>
            </DialogActions>
        </div></Paper>
    );
};
/* ======================== */
// PROPS
/* ======================== */
PanelEditor.propTypes = {
    index: PropTypes.number,
    panelCount: PropTypes.number,
    isOpen: PropTypes.bool.isRequired,
    globe: PropTypes.object.isRequired,
    storyId: PropTypes.string.isRequired,
    panel: PropTypes.shape({
        title: PropTypes.string,
        slug: PropTypes.string,
        content: PropTypes.string
    }),
    // Callbacks
    onChange: PropTypes.func,
    onUpdate: PropTypes.func,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
    // Functions
    createPanel: PropTypes.func.isRequired,
    updatePanel: PropTypes.func.isRequired
};
PanelEditor.displayName = 'PanelEditor';
/* ========================================== */
// Redux
/* ========================================== */
const mapStateToProps = (store) => {
    return {
        globe: getGlobe(store),
    };
};
const mapDispatchToProps = dispatch => {
    return bindActionCreators(Object.assign({ updatePanel, createPanel, updateMapState, dispatch }), dispatch);
};
/* ========================================== */
// Export
/* ========================================== */
export default connect(mapStateToProps, mapDispatchToProps)(PanelEditor);